type MultiLangName = {
  en?: string
  uz?: string
  ru?: string
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function isUuid(value: string | undefined | null): boolean {
  if (!value) return false
  return UUID_RE.test(value)
}

/**
 * Locale-stable slug from free text.
 * Keeps letters/numbers (incl. Cyrillic); collapses other runs to `-`.
 */
export function slugify(text: string | undefined | null): string {
  if (!text) return ''
  return text
    .normalize('NFKC')
    .toLowerCase()
    .trim()
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '')
}

/** Prefer en → uz → ru so the URL does not change with UI locale. */
export function slugFromMultiLang(name: MultiLangName | undefined | null): string {
  if (!name) return ''
  return slugify(name.en || name.uz || name.ru || '')
}

type ShipperCategoryLike = {
  id: string
  name?: MultiLangName
}

/**
 * Human-readable category key for query params.
 * Derived from name (API has no slug). Falls back to id if name is empty.
 */
export function getShipperCategorySlug(category: ShipperCategoryLike): string {
  return slugFromMultiLang(category.name) || category.id
}

export function resolveShipperCategoryId(
  value: string | undefined | null,
  categories: ShipperCategoryLike[],
): string {
  if (!value) return ''

  const byId = categories.find((item) => item.id === value)
  if (byId) return byId.id

  if (isUuid(value)) return value

  const bySlug = categories.find(
    (item) => getShipperCategorySlug(item) === value,
  )
  if (bySlug) return bySlug.id

  return value
}

/** True when `value` refers to this category (by id or derived slug). */
export function matchesShipperCategory(
  value: string | undefined | null,
  category: ShipperCategoryLike,
): boolean {
  if (!value) return false
  return value === category.id || value === getShipperCategorySlug(category)
}
